import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { KeyRound, Plus, RefreshCw, AlertTriangle } from "lucide-react";
import { theme, card, btnPrimary, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useFrameworks } from "../context/FrameworksContext";
import { iso27001Citation } from "../lib/standardCitation";
import { LoadingBlock } from "../components/Spinner";
import { PortalLink } from "../components/PortalLink";
import { PolicySaysChip } from "../components/PolicySaysChip";
import { NextSteps } from "../components/NextSteps";
import { DetailFields, DetailSection } from "../components/RecordView";
import {
  Check, EmptyState, Field, ModalHeader, ModalOverlay, MultiPick, StatusPill, dangerBtn, fmtDateOnly, fmtDateTime, input, relativeDue,
  secondaryBtn, tableHeadRow, textarea, type AttentionItem,
} from "./resilience/shared";

// Access reviews — managers confirm that each person still needs each group membership and
// application assignment the directory gives them (backend/src/routes/accessReviews.routes.ts,
// lib/accessReviews.ts). The platform reads the directory and never changes it: a removal becomes
// a corrective action or a leaver checklist. Reviewers answer on My Tasks. Needs people:write.

interface DirectoryRead { status: "queued" | "running" | "ok" | "error"; startedAt: string; completedAt: string | null; error: string | null; groups: number; apps: number; grants: number; truncated: boolean }
interface SourceView { key: "m365" | "google"; label: string; readable: boolean; connected: boolean; notReadableBecause: string | null; read: DirectoryRead | null }
interface Resource { source: string; resourceKind: "group" | "app"; resourceId: string; resourceName: string; people: number }
interface Counts { total: number; pending: number; kept: number; removed: number }
interface CampaignRow {
  id: string; ref: string; title: string; status: "active" | "completed" | "cancelled"; sources: string[]; resourceKinds: string[]; resourceIds: string[];
  startedAt: string; dueAt: string; completedAt: string | null; cancelledAt: string | null; cancelReason: string | null; counts: Counts;
}
interface View {
  sources: SourceView[]; resources: Resource[]; campaigns: CampaignRow[];
  cycleMonths: number; responseDays: number; removalDays: number; nextDueAt: string | null; attention: AttentionItem[];
}
interface Item {
  id: string; source: string; resourceKind: "group" | "app"; resourceName: string; principalName: string; principalEmail: string | null;
  reviewerUserId: string | null; reviewer: string | null; reviewerActive: boolean; reviewerBasisLabel: string; selfReviewed: boolean;
  decision: "pending" | "keep" | "remove"; reason: string | null; leaver: boolean; decidedAt: string | null;
  correctiveAction: { id: string; displayId: number | null; status: string } | null; lifecycleEventId: string | null;
}
interface CampaignDetail extends CampaignRow {
  directoryReadAt: string | null; startedBy: string | null; cancelledBy: string | null; items: Item[];
  members: Array<{ id: string; name: string; email: string }>;
}

const KIND_LABEL: Record<string, string> = { group: "Group", app: "Application" };
const STATUS_LABEL: Record<CampaignRow["status"], string> = { active: "Running", completed: "Completed", cancelled: "Cancelled" };
const SEV_COLOR: Record<AttentionItem["severity"], string> = { high: "var(--c-danger)", medium: "var(--c-warning)", low: "var(--c-borderStrong)" };
const th: React.CSSProperties = { padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 };
const td: React.CSSProperties = { padding: "10px 14px", color: theme.color.muted, verticalAlign: "top" };
const errorText: React.CSSProperties = { ...text.hint, color: theme.color.dangerText, marginTop: 10 };
const iconBtn: React.CSSProperties = { ...secondaryBtn, display: "flex", alignItems: "center", gap: 6 };
const caRef = (n: number | null) => `CA-${String(n ?? 0).padStart(3, "0")}`;

function Tile({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div style={{ ...card, padding: "14px 16px", height: "100%" }}>
      <div style={text.statLabel}>{label}</div>
      <div style={{ ...text.stat, color: theme.color.heading, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ ...text.hint, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function DecisionPill({ value }: { value: Item["decision"] }) {
  const s = value === "keep" ? { bg: theme.color.successBg, color: theme.color.successText, label: "Kept" }
    : value === "remove" ? { bg: theme.color.warningBg, color: theme.color.warningText, label: "Remove" }
    : { bg: theme.color.panelAlt, color: theme.color.muted, label: "Waiting" };
  return <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>{s.label}</span>;
}

export function AccessReviewsPage() {
  const { enrolled } = useFrameworks();
  const { user } = useCurrentUser();
  const canEdit = !!user?.permissions?.includes("people:write");
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<View | null>(null);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const [reading, setReading] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.get<View>("/access-reviews")); setError(""); }
    catch (e) { setError(apiErrorMessage(e, "Could not load access reviews.")); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  // While a read is queued or running, look again every few seconds.
  const readInFlight = !!data?.sources.some((s) => s.read && (s.read.status === "queued" || s.read.status === "running"));
  useEffect(() => {
    if (!readInFlight) return;
    const t = setInterval(() => { void load(); }, 4000);
    return () => clearInterval(t);
  }, [readInFlight, load]);

  const openId = searchParams.get("campaign");
  const setOpen = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("campaign", id); else next.delete("campaign");
    setSearchParams(next, { replace: true });
  };

  const running = data?.campaigns.find((c) => c.status === "active") ?? null;
  const completed = data?.campaigns.filter((c) => c.status === "completed") ?? [];

  const readDirectory = async (source: string) => {
    setReading(true); setError("");
    try { await api.post("/access-reviews/directory/read", { source }); await load(); }
    catch (e) { setError(apiErrorMessage(e, "The directory read could not be started.")); }
    finally { setReading(false); }
  };

  return (
    <div className="page-pad">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}>
            <KeyRound size={20} /> Access reviews
          </h1>
          <p style={{ ...pageSubtitle, maxWidth: 760 }}>
            Each person's manager confirms whether they still need the groups and applications the directory gives them,
            with a reason{iso27001Citation(enrolled, "A.5.18")}. The platform reads the directory and never changes it:
            a removal becomes a corrective action for someone to carry out.
          </p>
          <PolicySaysChip keys={["access.review_months", "access.review_response_days", "access.review_removal_days"]} />
        </div>
        {canEdit && data && !running && (
          <button type="button" style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }} onClick={() => setStarting(true)}>
            <Plus size={14} /> Start a campaign
          </button>
        )}
      </div>

      {error && <div style={{ ...errorText, marginTop: 0, marginBottom: 12 }}>{error}</div>}

      {!data ? (error ? null : <LoadingBlock />) : (
        <div style={{ display: "grid", gap: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
            <Tile label="Review cycle" value={`${data.cycleMonths} months`} sub="from Operating parameters" />
            <Tile label="Next review due" value={running ? "Running" : data.nextDueAt ? fmtDateOnly(data.nextDueAt) : "Now"} sub={running ? running.ref : completed.length ? "one cycle after the last" : "none completed yet"} />
            <Tile label="Running campaign" value={running ? `${running.counts.total - running.counts.pending} of ${running.counts.total}` : "None"} sub={running ? `answered, due ${fmtDateOnly(running.dueAt)}` : undefined} />
            <Tile label="Completed campaigns" value={completed.length} sub={completed[0]?.completedAt ? `last ${fmtDateOnly(completed[0].completedAt)}` : undefined} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20 }}>
            <div style={{ ...card, padding: 0 }}>
              <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "center", gap: 8, ...text.sectionTitle, margin: 0 }}>
                <AlertTriangle size={15} /> Needs attention
                <span style={{ ...text.hint, fontWeight: 400 }}>{data.attention.length}</span>
              </div>
              {data.attention.length === 0 ? (
                <div style={{ padding: 20, ...text.body, color: theme.color.muted }}>Nothing outstanding.</div>
              ) : data.attention.map((a) => (
                <PortalLink key={a.id} to={a.href} style={{ textDecoration: "none" }}>
                  <div style={{ display: "flex", gap: 12, padding: "11px 18px", borderBottom: "1px solid var(--c-border)", alignItems: "flex-start" }}>
                    <span style={{ width: 8, height: 8, borderRadius: 999, marginTop: 6, background: SEV_COLOR[a.severity], flexShrink: 0 }} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={text.itemTitle}>{a.title}</div>
                      <div style={{ ...text.hint, marginTop: 2 }}>{a.description}</div>
                    </div>
                    {a.dueAt && <span style={{ fontSize: text.meta.fontSize, color: a.severity === "high" ? theme.color.dangerText : theme.color.muted, whiteSpace: "nowrap" }}>{relativeDue(a.dueAt)}</span>}
                  </div>
                </PortalLink>
              ))}
            </div>

            <div style={{ ...card, padding: 18 }}>
              <div style={{ ...text.sectionTitle, margin: "0 0 10px" }}>Directories</div>
              <div style={{ display: "grid", gap: 14 }}>
                {data.sources.map((s) => (
                  <div key={s.key} style={{ display: "grid", gap: 4 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ ...text.body, color: theme.color.text, fontWeight: 600 }}>{s.label}</span>
                      {s.readable && canEdit && (
                        <button type="button" style={iconBtn} disabled={reading || readInFlight || !s.connected} onClick={() => void readDirectory(s.key)}>
                          <RefreshCw size={13} /> {readInFlight ? "Reading..." : "Read the directory"}
                        </button>
                      )}
                    </div>
                    {!s.readable ? (
                      <div style={text.hint}>{s.notReadableBecause}</div>
                    ) : !s.connected && !s.read ? (
                      <div style={text.hint}>Connect the <PortalLink to="/m365-security" style={{ color: "var(--c-accentText)" }}>Microsoft 365 Security connector</PortalLink> to read groups and application assignments. It needs no new permission.</div>
                    ) : s.read ? (
                      <div style={text.hint}>
                        {s.read.status === "ok" && <>Last read {fmtDateTime(s.read.completedAt)}: {s.read.groups} groups, {s.read.apps} applications, {s.read.grants} access rights.{s.read.truncated ? " Some access was not read: the directory is larger than one read takes in." : ""}</>}
                        {(s.read.status === "queued" || s.read.status === "running") && <>Reading the directory...</>}
                        {s.read.status === "error" && <span style={{ color: theme.color.dangerText }}>The last read failed: {s.read.error}</span>}
                      </div>
                    ) : <div style={text.hint}>Not read yet.</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ ...card, padding: 0, overflowX: "auto" }}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--c-border)", ...text.sectionTitle, margin: 0 }}>Campaigns</div>
            {data.campaigns.length === 0 ? (
              <div style={{ padding: 18 }}>
                <EmptyState title="No access reviews yet" detail="Read the directory, then start a campaign: each person's manager is asked to keep or remove every group and application they hold." />
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                <thead><tr style={tableHeadRow}>
                  <th style={th}>Ref</th><th style={th}>Campaign</th><th style={th}>Status</th><th style={th}>Started</th><th style={th}>Due</th><th style={th}>Answered</th><th style={th}>Kept</th><th style={th}>Removed</th>
                </tr></thead>
                <tbody>
                  {data.campaigns.map((c) => (
                    <tr key={c.id} onClick={() => setOpen(c.id)} style={{ cursor: "pointer", borderBottom: "1px solid var(--c-border)" }}>
                      <td style={{ ...td, color: theme.color.text, fontWeight: 600 }}>{c.ref}</td>
                      <td style={{ ...td, color: theme.color.text }}>{c.title}</td>
                      <td style={td}><StatusPill value={c.status === "active" ? "open" : c.status === "completed" ? "closed" : "cancelled"} label={STATUS_LABEL[c.status]} /></td>
                      <td style={td}>{fmtDateOnly(c.startedAt)}</td>
                      <td style={{ ...td, color: c.status === "active" && c.counts.pending > 0 && new Date(c.dueAt).getTime() < Date.now() ? theme.color.dangerText : td.color }}>{fmtDateOnly(c.dueAt)}</td>
                      <td style={td}>{c.counts.total - c.counts.pending} of {c.counts.total}</td>
                      <td style={td}>{c.counts.kept}</td>
                      <td style={td}>{c.counts.removed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {starting && data && <StartCampaign view={data} onClose={() => setStarting(false)} onStarted={(id) => { setStarting(false); void load(); setOpen(id); }} />}
      {openId && <CampaignView id={openId} canEdit={canEdit} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}

function StartCampaign({ view, onClose, onStarted }: { view: View; onClose: () => void; onStarted: (id: string) => void }) {
  const readable = view.sources.filter((s) => s.readable && s.read?.status === "ok");
  const [title, setTitle] = useState(`Access review ${new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" })}`);
  const [sources, setSources] = useState<string[]>(readable.map((s) => s.key));
  const [kinds, setKinds] = useState<string[]>(["group", "app"]);
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const options = useMemo(
    () => view.resources.filter((r) => sources.includes(r.source) && kinds.includes(r.resourceKind)).map((r) => ({ id: r.resourceId, name: `${r.resourceName} (${KIND_LABEL[r.resourceKind]}, ${r.people})` })),
    [view.resources, sources, kinds],
  );
  const toggle = (list: string[], v: string, on: boolean) => (on ? [...new Set([...list, v])] : list.filter((x) => x !== v));

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const res = await api.post<{ id: string; items: number }>("/access-reviews", { title, sources, resourceKinds: kinds, resourceIds: picked.filter((id) => options.some((o) => o.id === id)) });
      onStarted(res.id);
    } catch (e) { setError(apiErrorMessage(e, "The campaign could not be started.")); }
    finally { setBusy(false); }
  };

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title="Start an access review" onClose={onClose} />
      <div style={{ padding: 20, display: "grid", gap: 14 }}>
        {readable.length === 0 ? (
          <div style={text.body}>Read a directory first: the campaign is built from the last read.</div>
        ) : (
          <>
            <Field label="Title"><input style={input} value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} /></Field>
            <Field label="Directories">
              <div style={{ display: "grid", gap: 6 }}>
                {readable.map((s) => <Check key={s.key} checked={sources.includes(s.key)} onChange={(on) => setSources(toggle(sources, s.key, on))} label={s.label} help={s.read?.completedAt ? `Read ${fmtDateTime(s.read.completedAt)}` : undefined} />)}
              </div>
            </Field>
            <Field label="What to review">
              <div style={{ display: "grid", gap: 6 }}>
                <Check checked={kinds.includes("group")} onChange={(on) => setKinds(toggle(kinds, "group", on))} label="Group memberships" help="Security and Microsoft 365 groups, direct members" />
                <Check checked={kinds.includes("app")} onChange={(on) => setKinds(toggle(kinds, "app", on))} label="Application assignments" help="People assigned to enterprise applications directly; access through a group is reviewed on the group" />
              </div>
            </Field>
            <Field label="Only these groups and applications" help="Leave empty to review everything the read found.">
              <MultiPick options={options} selected={picked} onChange={setPicked} empty="Nothing found in this scope." />
            </Field>
            <div style={text.hint}>
              Each item goes to the person's manager in the directory, else an owner of the group or application, else an administrator.
              Reviewers have {view.responseDays} days to answer on My Tasks; a removal is due {view.removalDays} days after it is decided.
            </div>
          </>
        )}
        {error && <div style={errorText}>{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
          <button type="button" style={btnPrimary} disabled={busy || readable.length === 0 || sources.length === 0 || kinds.length === 0} onClick={() => void submit()}>
            {busy ? "Starting..." : "Start campaign"}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

function CampaignView({ id, canEdit, onClose, onChanged }: { id: string; canEdit: boolean; onClose: () => void; onChanged: () => void }) {
  const [c, setC] = useState<CampaignDetail | null>(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<"" | Item["decision"]>("");
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try { setC(await api.get<CampaignDetail>(`/access-reviews/${id}`)); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the campaign.")); }
  }, [id]);
  useEffect(() => { void load(); }, [load]);

  const reassign = async (itemId: string, reviewerUserId: string) => {
    setError("");
    try { await api.patch(`/access-reviews/${id}/items/${itemId}`, { reviewerUserId }); await load(); }
    catch (e) { setError(apiErrorMessage(e, "The item could not be reassigned.")); }
  };
  const cancel = async () => {
    setError("");
    try { await api.post(`/access-reviews/${id}/cancel`, { reason }); setCancelling(false); await load(); onChanged(); }
    catch (e) { setError(apiErrorMessage(e, "The campaign could not be cancelled.")); }
  };

  const items = (c?.items ?? []).filter((i) => !filter || i.decision === filter);
  const active = c?.status === "active";
  const overdue = !!c && active && new Date(c.dueAt).getTime() < Date.now();
  const orphaned = (c?.items ?? []).filter((i) => i.decision === "pending" && (!i.reviewerUserId || !i.reviewerActive)).length;

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title={c ? `${c.ref} ${c.title}` : "Access review"} onClose={onClose} right={c ? <StatusPill value={c.status === "active" ? "open" : c.status === "completed" ? "closed" : "cancelled"} label={STATUS_LABEL[c.status]} /> : undefined} />
      <div style={{ padding: 20, display: "grid", gap: 16 }}>
        {!c ? (error ? <div style={errorText}>{error}</div> : <LoadingBlock />) : (
          <>
            {active && (
              <NextSteps items={[
                { done: c.counts.pending === 0, label: `Every item answered (${c.counts.total - c.counts.pending} of ${c.counts.total})`, hint: `Due ${fmtDateOnly(c.dueAt)}`, urgent: overdue },
                ...(orphaned ? [{ done: false, label: `${orphaned} item${orphaned === 1 ? "" : "s"} with a reviewer who can no longer answer`, hint: "Reassign them below", urgent: true }] : []),
              ]} />
            )}
            <DetailSection title="Campaign">
              <DetailFields fields={[
                { label: "Started", value: `${fmtDateOnly(c.startedAt)}${c.startedBy ? ` by ${c.startedBy}` : ""}` },
                { label: "Due", value: fmtDateOnly(c.dueAt) },
                { label: "Directory read", value: c.directoryReadAt ? fmtDateTime(c.directoryReadAt) : "Not recorded" },
                { label: "Scope", value: `${c.resourceKinds.map((k) => (k === "group" ? "group memberships" : "application assignments")).join(" and ")}${c.resourceIds.length ? ` (${c.resourceIds.length} selected)` : ""}` },
                { label: "Answered", value: `${c.counts.total - c.counts.pending} of ${c.counts.total}: ${c.counts.kept} kept, ${c.counts.removed} to remove` },
                ...(c.completedAt ? [{ label: "Completed", value: `${fmtDateOnly(c.completedAt)} - filed in the Evidence Repository` }] : []),
                ...(c.cancelledAt ? [{ label: "Cancelled", value: `${fmtDateOnly(c.cancelledAt)}${c.cancelledBy ? ` by ${c.cancelledBy}` : ""}: ${c.cancelReason ?? ""}` }] : []),
              ]} />
            </DetailSection>

            <DetailSection title="Items" aside={
              <select style={{ ...input, width: "auto" }} value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)}>
                <option value="">All</option><option value="pending">Waiting</option><option value="keep">Kept</option><option value="remove">Remove</option>
              </select>
            }>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                  <thead><tr style={tableHeadRow}>
                    <th style={th}>Person</th><th style={th}>Access</th><th style={th}>Reviewer</th><th style={th}>Decision</th><th style={th}>Reason</th><th style={th}>Follow-up</th>
                  </tr></thead>
                  <tbody>
                    {items.map((i) => (
                      <tr key={i.id} style={{ borderBottom: "1px solid var(--c-border)" }}>
                        <td style={{ ...td, color: theme.color.text }}>{i.principalName}{i.principalEmail && i.principalEmail !== i.principalName && <div style={text.hint}>{i.principalEmail}</div>}</td>
                        <td style={td}>{i.resourceName}<div style={text.hint}>{KIND_LABEL[i.resourceKind]}</div></td>
                        <td style={td}>
                          {canEdit && active && i.decision === "pending" ? (
                            <select style={{ ...input, width: "auto", maxWidth: 220 }} value={i.reviewerUserId ?? ""} onChange={(e) => void reassign(i.id, e.target.value)}>
                              {!i.reviewerActive && <option value={i.reviewerUserId ?? ""}>{i.reviewer ?? "Nobody"} (no longer active)</option>}
                              {c.members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                            </select>
                          ) : <span style={{ color: theme.color.text }}>{i.reviewer ?? "-"}</span>}
                          <div style={text.hint}>{i.reviewerBasisLabel}{i.selfReviewed ? " (own access: no other administrator)" : ""}</div>
                        </td>
                        <td style={td}><DecisionPill value={i.decision} />{i.leaver && <div style={text.hint}>Has left</div>}</td>
                        <td style={td}>{i.reason ?? ""}{i.decidedAt && <div style={text.hint}>{fmtDateOnly(i.decidedAt)}</div>}</td>
                        <td style={td}>
                          {i.correctiveAction && <PortalLink to={`/improvement?action=${i.correctiveAction.id}`} style={{ color: "var(--c-accentText)" }}>{caRef(i.correctiveAction.displayId)}</PortalLink>}
                          {i.correctiveAction && <div style={text.hint}>{i.correctiveAction.status}</div>}
                          {i.lifecycleEventId && <PortalLink to={`/people?event=${i.lifecycleEventId}`} style={{ color: "var(--c-accentText)" }}>Leaver checklist</PortalLink>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DetailSection>

            {error && <div style={errorText}>{error}</div>}
            {canEdit && active && (
              cancelling ? (
                <div style={{ display: "grid", gap: 8 }}>
                  <Field label="Why is the campaign being cancelled?"><textarea style={textarea} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <button type="button" style={secondaryBtn} onClick={() => setCancelling(false)}>Keep it running</button>
                    <button type="button" style={dangerBtn} onClick={() => void cancel()}>Cancel campaign</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button type="button" style={dangerBtn} onClick={() => setCancelling(true)}>Cancel campaign</button>
                </div>
              )
            )}
            <div style={text.hint}>A campaign is kept once completed or cancelled; nothing deletes one.</div>
          </>
        )}
      </div>
    </ModalOverlay>
  );
}
